import { CalendarCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { mensajeDe } from '@/api/errors';
import { PermissionGate } from '@/auth/PermissionGate';
import { TituloBloque } from '@/components/BloquesConsalud';
import { AVATARES } from '@/lib/avatares';
import { cn } from '@/lib/utils';

import { Cargando, ErrorCarga, TabsModulo } from '../curso/componentes';

import {
  DIAS,
  fechaDia,
  useGuardarPlan,
  useOpcionesPlan,
  usePlan,
  useVistaPrevia,
  type PlanOptions,
  type StudyPlan,
} from './queries';

/** "120 días de estudio · 2 clases por día" o, si hay menos temas que días, cuántos días se usan. */
function resumenRonda(p: {
  studyDays: number;
  topics: number;
  perDay: { min: number; max: number };
}) {
  if (p.perDay.min === 0) {
    return `${String(p.studyDays)} días de estudio · ${String(p.topics)} temas, uno por día (los últimos días quedan libres para repasar)`;
  }
  const rango =
    p.perDay.min === p.perDay.max
      ? String(p.perDay.max)
      : `${String(p.perDay.min)} a ${String(p.perDay.max)}`;
  return `${String(p.studyDays)} días de estudio · ${rango} ${p.perDay.max === 1 ? 'clase' : 'clases'} por día`;
}

/** Lunes a domingo (el valor es el de la API: 0 = domingo). */
const SEMANA = [1, 2, 3, 4, 5, 6, 0];
const AREAS = ['Ginecología', 'Cirugía', 'Pediatría', 'Medicina Interna'];

/** Encabezado de paso numerado (círculo verde como en el legacy). */
function Paso({ n, children }: { n: number; children: string }) {
  return (
    <h2 className="flex items-center gap-2 font-extrabold text-azul-texto">
      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-verde-500 text-sm text-white">
        {n}
      </span>
      {children}
    </h2>
  );
}

/** Organizar el estudio (port de `temario/organizar`): la API calcula y valida el plan. */
export function OrganizarPage() {
  return (
    <PermissionGate permiso="study_plan.access" modulo="Organizar mi estudio">
      <Carga />
    </PermissionGate>
  );
}

function Carga() {
  const opciones = useOpcionesPlan();
  const plan = usePlan();
  const [params] = useSearchParams();
  if (opciones.isPending || plan.isPending) return <Cargando alto="h-96" />;
  // Con un plan guardado se va directo al calendario; solo "Configurar" (desde el plan) reabre esto.
  if (plan.data && !params.has('reconfigurar')) {
    return <Navigate to="/curso-enarm/temario/plan" replace />;
  }
  if (opciones.error) return <ErrorCarga error={opciones.error} />;
  return <Organizar o={opciones.data} actual={plan.data ?? null} />;
}

function Organizar({ o, actual }: { o: PlanOptions; actual: StudyPlan | null }) {
  const navigate = useNavigate();
  const previa = useVistaPrevia();
  const guardar = useGuardarPlan();
  const [rondas, setRondas] = useState<{ start: string; end: string }[]>(
    () => actual?.config.rounds ?? o.suggestedRounds['1'] ?? [],
  );
  const [dias, setDias] = useState<number[]>(() => actual?.config.weekdays ?? [1, 2, 3, 4, 5]);
  const config = { rounds: rondas, weekdays: dias };
  const clave = JSON.stringify(config);

  // Vista previa del servidor cada vez que cambia la configuración (con pausa de 400 ms).
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (rondas.length && dias.length) previa.mutate(JSON.parse(clave) as typeof config);
    }, 400);
    return () => {
      window.clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave]);

  const elegirRondas = (n: number) => setRondas(o.suggestedRounds[String(n)] ?? []);
  const fecha = (i: number, campo: 'start' | 'end', v: string) =>
    setRondas((rs) => rs.map((r, k) => (k === i ? { ...r, [campo]: v } : r)));
  const error = previa.error ? mensajeDe(previa.error) : null;

  return (
    <div className="space-y-6">
      <TabsModulo />
      <h1 className="titulo-seccion">
        <span className="subrayado-verde">Configurar mi plan de estudios</span>
      </h1>
      {/* "¿En qué consiste la configuración?" del legacy + la nota con la fecha del ENARM. */}
      <section className="panel-vidrio space-y-2 border-l-4 border-l-cian-500 text-sm">
        <h2 className="font-extrabold text-azul-texto">¿En qué consiste la configuración?</h2>
        <p className="text-texto-2">
          Con esta herramienta podrás obtener un plan de estudio personalizado para ti en función de
          tus tiempos y de los días en que desees estudiar.
        </p>
        {o.enarm.date && (
          <p>
            El ENARM {o.enarm.year ?? ''} será el <strong>{fechaDia(o.enarm.date, false)}</strong>
            {o.enarm.applicationDays.length > 0 && (
              <>
                {' '}
                (días de aplicación:{' '}
                {o.enarm.applicationDays.map((d) => fechaDia(d, false)).join(', ')})
              </>
            )}
            . Tu plan debe terminar antes. El temario tiene <strong>{o.topics} temas</strong>.
          </p>
        )}
      </section>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
        <div className="space-y-6">
          <section className="panel-vidrio space-y-3">
            <Paso n={1}>Selecciona el número de rondas que deseas tener</Paso>
            <div className="flex flex-wrap gap-2">
              {[1, 2, 3].map((n) => (
                <button
                  key={n}
                  type="button"
                  className="btn-modulo"
                  data-state={rondas.length === n ? 'active' : undefined}
                  onClick={() => elegirRondas(n)}
                >
                  {n} {n === 1 ? 'ronda' : 'rondas'}
                </button>
              ))}
            </div>
            <p className="text-xs text-texto-3 italic">
              Una ronda de estudio es equivalente a revisar una vez el temario.
            </p>
          </section>

          <section className="panel-vidrio space-y-3">
            <Paso n={2}>Selecciona tus días de estudio</Paso>
            <div className="flex flex-wrap gap-2">
              {SEMANA.map((d) => {
                const on = dias.includes(d);
                return (
                  <button
                    key={d}
                    type="button"
                    aria-pressed={on}
                    className="btn-modulo"
                    data-state={on ? 'active' : undefined}
                    onClick={() => setDias((ds) => (on ? ds.filter((x) => x !== d) : [...ds, d]))}
                  >
                    {DIAS[d]}
                  </button>
                );
              })}
            </div>
          </section>

          <section className="panel-vidrio space-y-4">
            <Paso n={3}>Selecciona tus fechas de estudio</Paso>
            <p className="text-sm text-texto-2">
              Las sugerimos en partes iguales hasta el ENARM; puedes ajustarlas.
            </p>
            {rondas.map((r, i) => {
              const p = previa.data?.[i];
              return (
                <div
                  key={i}
                  className="card-vidrio grid gap-3 p-4 sm:grid-cols-[auto_1fr_1fr] sm:items-end"
                >
                  <p className="font-extrabold">Ronda {i + 1}</p>
                  <label className="text-xs font-bold text-texto-3">
                    Inicio
                    <input
                      type="date"
                      className="input-vidrio mt-1 w-full"
                      value={r.start}
                      min={o.today}
                      max={o.enarm.date ?? undefined}
                      onChange={(e) => fecha(i, 'start', e.target.value)}
                    />
                  </label>
                  <label className="text-xs font-bold text-texto-3">
                    Fin
                    <input
                      type="date"
                      className="input-vidrio mt-1 w-full"
                      value={r.end}
                      min={r.start}
                      max={o.enarm.date ?? undefined}
                      onChange={(e) => fecha(i, 'end', e.target.value)}
                    />
                  </label>
                  {p && <p className="text-sm text-texto-2 sm:col-span-3">{resumenRonda(p)}</p>}
                </div>
              );
            })}
            {error && <p className="text-sm font-bold text-rojo-texto">{error}</p>}
          </section>
        </div>

        <aside className="panel-vidrio mt-12 space-y-4 self-start lg:mt-10">
          <TituloBloque avatar={AVATARES.doctora} className="pl-10">
            Confirma y crea tu calendario
          </TituloBloque>
          <p className="pt-2 text-sm text-texto-2">
            Antes de continuar es importante mencionar que este es el orden por áreas recomendado
            por Consalud para estudiar el temario; cada ronda lo recorre completo:
          </p>
          <ol className="space-y-2">
            {AREAS.map((a, i) => (
              <li key={a} className="flex items-center gap-3 font-bold">
                <span className="grid h-7 w-7 place-items-center rounded-full bg-azul-600 text-sm text-white">
                  {i + 1}
                </span>
                {a}
              </li>
            ))}
          </ol>
          <img
            src="/img/consalud-horizontal-color.png"
            alt="Consalud"
            className="solo-claro mx-auto my-6 h-10 w-auto"
          />
          <img
            src="/img/consalud-horizontal-blanco.png"
            alt="Consalud"
            className="solo-oscuro mx-auto my-6 h-10 w-auto"
          />
          {actual && (
            <p className="text-xs text-texto-3">Al guardar reemplazarás tu plan actual.</p>
          )}
          <button
            type="button"
            className={cn('btn-cs btn-exito w-full', !error && !guardar.isPending && 'pulse-cta')}
            disabled={!!error || guardar.isPending || rondas.length === 0 || dias.length === 0}
            onClick={() =>
              guardar.mutate(config, {
                onSuccess: () => {
                  toast.success('¡Listo! Creamos tu plan de estudio.');
                  void navigate('/curso-enarm/temario/plan');
                },
                onError: (e) => toast.error(mensajeDe(e)),
              })
            }
          >
            <CalendarCheck size={18} />{' '}
            {guardar.isPending ? 'Creando…' : 'Continuar y crear mi calendario'}
          </button>
        </aside>
      </div>
    </div>
  );
}
